import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Code, 
  FileText, 
  GitBranch, 
  Search, 
  Shield, 
  Zap, 
  Database, 
  Settings,
  CheckCircle,
  AlertTriangle,
  Eye,
  Wrench
} from "lucide-react";

export default function CodePage() {
  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Code className="w-8 h-8 text-shiftleft-600" />
          Code Analysis & Development
        </h1>
        <p className="text-gray-600 mt-1">
          Comprehensive code analysis, review, and development tools for SQL Server applications and stored procedures
        </p>
      </div>

      {/* Code Analysis Tabs */}
      <Tabs defaultValue="static-analysis" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="static-analysis">Static Analysis</TabsTrigger>
          <TabsTrigger value="code-review">Code Review</TabsTrigger>
          <TabsTrigger value="stored-procs">Stored Procedures</TabsTrigger>
          <TabsTrigger value="security-scan">Security Scan</TabsTrigger>
          <TabsTrigger value="performance">Performance Analysis</TabsTrigger>
        </TabsList>

        {/* Static Analysis */}
        <TabsContent value="static-analysis" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-shiftleft-600" />
                  Code Quality Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Automated analysis of SQL code quality, complexity, and maintainability metrics.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-shiftleft-50 rounded-lg">
                    <div className="text-sm font-medium text-shiftleft-800">Complexity Score</div>
                    <div className="text-2xl font-bold text-shiftleft-600">7.2</div>
                    <div className="text-xs text-shiftleft-600">Medium Complexity</div>
                  </div>
                  
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="text-sm font-medium text-green-800">Maintainability</div>
                    <div className="text-2xl font-bold text-green-600">85%</div>
                    <div className="text-xs text-green-600">Good</div>
                  </div>
                </div>
                
                <Button className="w-full">Run Static Analysis</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  Code Coverage Report
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Track code coverage metrics and identify untested code paths.
                </p>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Stored Procedures</span>
                    <Badge variant="outline">78% Coverage</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Functions</span>
                    <Badge variant="outline">92% Coverage</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Triggers</span>
                    <Badge variant="outline">65% Coverage</Badge>
                  </div>
                </div>
                
                <Button variant="outline" className="w-full">View Coverage Report</Button>
              </CardContent>
            </Card>
          </div>

          {/* Code Issues Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                Code Issues Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">12</div>
                  <div className="text-sm text-red-800">Critical Issues</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">28</div>
                  <div className="text-sm text-orange-800">Warnings</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">45</div>
                  <div className="text-sm text-yellow-800">Code Smells</div>
                </div>
                <div className="text-center p-4 bg-shiftleft-50 rounded-lg">
                  <div className="text-2xl font-bold text-shiftleft-600">156</div>
                  <div className="text-sm text-shiftleft-800">Suggestions</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Code Review */}
        <TabsContent value="code-review" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-purple-600" />
                  Review Guidelines
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Standardized code review guidelines and best practices for SQL Server development.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Naming Conventions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Error Handling</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Performance Patterns</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Security Practices</span>
                  </div>
                </div>
                
                <Button className="w-full">View Guidelines</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-indigo-600" />
                  Active Reviews
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Current code reviews pending approval and feedback.
                </p>
                
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <div className="font-medium">usp_al_import_850</div>
                    <div className="text-sm text-gray-600">Performance optimization review</div>
                    <Badge variant="outline" className="mt-1">Pending</Badge>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="font-medium">Customer Data Functions</div>
                    <div className="text-sm text-gray-600">Security compliance review</div>
                    <Badge variant="outline" className="mt-1">In Progress</Badge>
                  </div>
                </div>
                
                <Button variant="outline" className="w-full">View All Reviews</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Stored Procedures */}
        <TabsContent value="stored-procs" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-shiftleft-600" />
                  Procedure Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Comprehensive analysis of stored procedures including performance, complexity, and dependencies.
                </p>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">usp_al_import_850</span>
                    <Badge variant="outline">Complex</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">usp_customer_lookup</span>
                    <Badge variant="outline">Simple</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">usp_order_processing</span>
                    <Badge variant="outline">Medium</Badge>
                  </div>
                </div>
                
                <Button className="w-full">Analyze All Procedures</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-600" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Real-time performance metrics and execution statistics for stored procedures.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <div className="text-sm font-medium text-yellow-800">Avg Execution</div>
                    <div className="text-2xl font-bold text-yellow-600">2.3s</div>
                  </div>
                  
                  <div className="p-3 bg-red-50 rounded-lg">
                    <div className="text-sm font-medium text-red-800">CPU Usage</div>
                    <div className="text-2xl font-bold text-red-600">78%</div>
                  </div>
                </div>
                
                <Button variant="outline" className="w-full">View Performance Dashboard</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Security Scan */}
        <TabsContent value="security-scan" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-600" />
                  Security Vulnerabilities
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Automated security scanning for SQL injection, privilege escalation, and data exposure risks.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div>
                      <div className="font-medium text-red-800">SQL Injection Risk</div>
                      <div className="text-sm text-red-600">3 procedures affected</div>
                    </div>
                    <Badge variant="destructive">High</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <div>
                      <div className="font-medium text-orange-800">Privilege Issues</div>
                      <div className="text-sm text-orange-600">5 procedures affected</div>
                    </div>
                    <Badge variant="outline">Medium</Badge>
                  </div>
                </div>
                
                <Button className="w-full">Run Security Scan</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-green-600" />
                  Compliance Check
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Verify compliance with security standards and regulatory requirements.
                </p>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">GDPR Compliance</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700">Compliant</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">SOX Requirements</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700">Compliant</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">PCI DSS</span>
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Review Needed</Badge>
                  </div>
                </div>
                
                <Button variant="outline" className="w-full">Generate Compliance Report</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Performance Analysis */}
        <TabsContent value="performance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-shiftleft-600" />
                  Code Optimization
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Identify performance bottlenecks and optimization opportunities in SQL code.
                </p>
                
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <div className="font-medium">Missing Index Opportunities</div>
                    <div className="text-sm text-gray-600">12 potential indexes identified</div>
                    <Badge variant="outline" className="mt-1">High Impact</Badge>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="font-medium">Query Optimization</div>
                    <div className="text-sm text-gray-600">8 queries can be optimized</div>
                    <Badge variant="outline" className="mt-1">Medium Impact</Badge>
                  </div>
                </div>
                
                <Button className="w-full">Run Performance Analysis</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-purple-600" />
                  Execution Plans
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Analyze execution plans and identify expensive operations.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="text-sm font-medium text-purple-800">Plan Cache Hit</div>
                    <div className="text-2xl font-bold text-purple-600">94%</div>
                  </div>
                  
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <div className="text-sm font-medium text-orange-800">Expensive Ops</div>
                    <div className="text-2xl font-bold text-orange-600">23</div>
                  </div>
                </div>
                
                <Button variant="outline" className="w-full">View Execution Plans</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}